import Konva from "konva";
import { FloorShape } from "../models/floorPlan";
import { DynamicValuesEnum } from "src/core/enums/dynamic-values.enum";

interface LabelDynamics {
  fontSize: number;
  height: number;
  yOffset: number;
  padding: number;
  textPadding: number;
  strokeWidth: number;
  cornerRadius: number;
  shadowBlur: number;
  shadowColor: number;
  shadowOffset: number;
}

export interface Layout {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface CalculateLabelLayoutsParams {
  showLabels: boolean;
  showAssets: boolean;
  shapes: FloorShape[];
  labelDynamics: LabelDynamics;
  preventLabelOverlap: boolean;
  dynamicCircle: number;
  overrideDefaultSizeValue?: any;
}

/**
 * Calculates non-overlapping label layouts for floor plan shapes
 * @param params Configuration object containing shapes, display settings, and dynamic values
 * @returns Map of shape IDs to their calculated label layouts
 */
export const calculateLabelLayouts = ({
  showLabels,
  showAssets,
  shapes,
  labelDynamics,
  preventLabelOverlap,
  dynamicCircle,
  overrideDefaultSizeValue,
}: CalculateLabelLayoutsParams): Map<string | number, Layout> => {
  if (!showLabels || !showAssets || !shapes?.length) {
    return new Map<string | number, Layout>();
  }

  const placed: Layout[] = [];
  const map = new Map<string | number, Layout>();

  const collides = (a: Layout, b: Layout) =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

  const measureTextWidth = (text: string) => {
    // Use Konva.Text for accurate width in current font settings
    const t = new Konva.Text({
      text,
      fontSize: labelDynamics.fontSize,
      fontStyle: "bold",
    });
    return t.width();
  };

  for (const shape of shapes) {
    const labelName = shape?.labelDisplayName || shape?.sensorDisplayName || shape?.assetCode;
    if (!labelName) continue;

    const w =
      measureTextWidth(labelName) + labelDynamics?.padding;
    const h = labelDynamics?.height;

    // Keep labels very close to their node edge (using node size)
    const isGateway =
      String(shape?.assetType || "").toLowerCase() === "gateway";
    const baseSize = isGateway
      ? DynamicValuesEnum.GATEWAY_SIZE +
        (overrideDefaultSizeValue?.gateway?.size || 0)
      : DynamicValuesEnum.CIRCLE + (overrideDefaultSizeValue?.circle || 0); // original icon size definition
    const actualSize = isGateway
      ? DynamicValuesEnum.GATEWAY_SIZE +
        (overrideDefaultSizeValue?.gateway?.size || 0)
      : dynamicCircle; // rendered size
    const r = actualSize / 2; // node "radius"
    const s = 0.5; // spacing from node edge (tighter)

    const centerX = shape.x + baseSize / 2;
    const centerY = shape.y + baseSize / 2;

    // Candidate placements around the anchor (shape.x, shape.y)
    const candidates: Layout[] = [
      { x: centerX - w / 2, y: centerY - r - h - s, w, h }, // above
      { x: centerX - w / 2, y: centerY + r + s, w, h }, // below
      { x: centerX - r - w - s, y: centerY - h / 2, w, h }, // left
      { x: centerX + r + s, y: centerY - h / 2, w, h }, // right
      { x: centerX - r - w - s, y: centerY - r - h - s, w, h }, // top-left
      { x: centerX + r + s, y: centerY - r - h - s, w, h }, // top-right
      { x: centerX - r - w - s, y: centerY + r + s, w, h }, // bottom-left
      { x: centerX + r + s, y: centerY + r + s, w, h }, // bottom-right
    ];

    let chosen: Layout | null = null;
    if (!preventLabelOverlap) {
      // No overlap adjustment: pick the first candidate (above)
      chosen = candidates[0];
    } else {
      for (const cand of candidates) {
        if (!placed.some((p) => collides(cand, p))) {
          chosen = cand;
          break;
        }
      }

      // Fallback: nudge upwards until no collision or max attempts
      if (!chosen) {
        const base: Layout = {
          x: centerX - w / 2,
          y: centerY - r - h - s,
          w,
          h,
        };
        let attempt = 0;
        let temp = { ...base };
        while (attempt < 20 && placed.some((p) => collides(temp, p))) {
          temp = { ...temp, y: temp.y - (h + 4) };
          attempt++;
        }
        chosen = temp;
      }
    }

    placed.push(chosen);
    map.set(shape.id, chosen);
  }

  return map;
};
